<template>
  <div class="stage_selector flex items-center gap-1.5 flex-wrap" role="toolbar" :aria-label="messages.stage.toolbarLabel" :aria-busy="busy || renamePending">
    <span class="font-mono text-xs uppercase tracking-widest text-text-muted px-1">{{ messages.stage.stage }}</span>
    <button type="button" :disabled="busy" class="inline-flex min-h-11 items-center px-2.5 rounded-md border text-xs font-bold tracking-wide uppercase transition-[color,border-color,background-color] disabled:opacity-60 disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6" :class="activeStageId === null ? 'border-accent text-accent bg-accent/15' : 'border-border text-text-secondary bg-transparent hover:border-accent hover:text-accent'" :aria-pressed="activeStageId === null ? 'true' : 'false'" @click="emit('select', null)">{{ messages.stage.all }}</button>
    <button v-for="stage in stages" :key="stage.id" type="button" :disabled="busy" class="inline-flex min-h-11 items-center px-2.5 rounded-md border text-xs font-bold tracking-wide uppercase transition-[color,border-color,background-color] disabled:opacity-60 disabled:cursor-wait focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6" :class="raidStageTabClass(stage)" :aria-pressed="activeStageId === stage.id ? 'true' : 'false'" :title="raidStageChipTitle(stage)" :aria-label="raidStageChipAriaLabel(stage)" @click="emit('select', stage.id)">{{ stage.name || stageKindLabel(stage.kind) }}</button>
    <span v-if="busy" class="font-mono text-2xs uppercase tracking-wide text-accent" role="status">{{ messages.stage.loading }}</span>
    <button v-if="isOfficer && stages.length < MAX_STAGES_PER_RAID" type="button" :disabled="busy" class="inline-flex min-h-11 items-center px-2 rounded-md border border-dashed border-border text-xs font-bold tracking-wide uppercase text-text-muted bg-transparent transition-[color,border-color] disabled:opacity-60 hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6" @click="emit('add')" :aria-label="messages.stage.addLabel">{{ messages.stage.add }}</button>
    <button v-if="isOfficer && activeCustomStage !== null && editingStageId === null" type="button" :disabled="busy" class="inline-flex min-h-11 items-center px-2 rounded-md border border-border text-xs font-bold tracking-wide uppercase text-text-secondary bg-transparent transition-[color,border-color] disabled:opacity-60 disabled:cursor-wait hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6" @click="startRename" :aria-label="messages.stage.renameLabel">{{ messages.stage.rename }}</button>
    <button v-if="isOfficer && activeStageId !== null" type="button" :disabled="busy" class="inline-flex min-h-11 items-center px-2 rounded-md text-xs font-bold tracking-wide uppercase text-danger bg-transparent transition-colors disabled:opacity-60 hover:text-danger focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger sm:min-h-6" @click="emit('remove')" :aria-label="messages.stage.removeLabel">{{ messages.stage.remove }}</button>
  </div>
  <form v-if="isOfficer && editingStageId !== null && activeCustomStage !== null" class="stage_rename flex flex-wrap items-start gap-2 rounded-md border border-border bg-bg-elevated p-2" :aria-label="messages.stage.renameLabel" @submit.prevent="saveRename" @keydown.esc="closeRename">
    <label :for="customStageNameInputId" class="pt-2 font-mono text-2xs uppercase tracking-wide text-text-muted">{{ messages.stage.name }}</label>
    <div class="min-w-48 flex-1">
      <AppInput :id="customStageNameInputId" v-model="renameName" :disabled="busy || renamePending" :error="renameError !== null" :max="MAX_NAME_LEN" :input-aria-describedby="renameError === null ? undefined : customStageNameErrorId" :input-aria-label="messages.stage.name" autofocus />
      <p v-if="renameError !== null" :id="customStageNameErrorId" class="mt-1 text-xs text-danger" role="alert">{{ renameError }}</p>
    </div>
    <span class="pt-2 font-mono text-2xs text-text-muted" aria-live="polite">{{ messages.stage.nameCount(renameNameLength, MAX_NAME_LEN) }}</span>
    <button type="submit" :disabled="busy || renamePending" class="inline-flex min-h-11 items-center rounded-md border border-accent bg-accent px-2 text-xs font-bold tracking-wide uppercase text-bg transition-colors disabled:opacity-60 disabled:cursor-wait hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6">{{ renamePending ? messages.stage.renaming : messages.stage.saveName }}</button>
    <button type="button" :disabled="renamePending" class="inline-flex min-h-11 items-center rounded-md border border-border px-2 text-xs font-bold tracking-wide uppercase text-text-secondary transition-[color,border-color] disabled:opacity-60 hover:border-accent hover:text-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent sm:min-h-6" @click="closeRename">{{ messages.stage.cancel }}</button>
  </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { AppInput } from '@/shared/ui'
import { MAX_NAME_LEN, MAX_STAGES_PER_RAID, RaidStageKind } from '@/features/gvg-planner/types'
import type { RaidStage, RaidStageId } from '@/features/gvg-planner/types'
import { useGvgPlannerStore } from '@/features/gvg-planner/stores/gvgPlannerStore'
import { calendarEn } from './locales/en.ts'
import { calendarRu } from './locales/ru.ts'

export interface StageChipStripProps {
  readonly stages: ReadonlyArray<RaidStage>
  readonly activeStageId: RaidStageId | null
  readonly isOfficer: boolean
  readonly busy: boolean
}

const props = defineProps<StageChipStripProps>()
const messages = useLocaleMessages(defineLocaleCatalog({ en: calendarEn, ru: calendarRu }))
const store = useGvgPlannerStore()
const emit = defineEmits<{
  (event: 'select', stageId: RaidStageId | null): void
  (event: 'add'): void
  (event: 'remove'): void
}>()
const customStageNameInputId = 'custom-stage-name'
const customStageNameErrorId = 'custom-stage-name-error'
const editingStageId = ref<RaidStageId | null>(null)
const renameName = ref('')
const renameError = ref<string | null>(null)
const renamePending = ref(false)

const activeCustomStage = computed<RaidStage | null>(() => {
  const stage = props.stages.find((candidate) => candidate.id === props.activeStageId)
  return stage?.kind === RaidStageKind.Custom ? stage : null
})
const renameNameLength = computed(() => Array.from(renameName.value).length)

watch(activeCustomStage, (stage) => {
  if (stage === null || stage.id !== editingStageId.value) closeRename()
})

/**
 * Display labels for the four game-stage kinds (plan 04). The backend enum
 * and the `forest` / `mid_push` / `late_defense` / `endgame` DB values are
 * unchanged — only the planner-facing label is swapped to the in-game
 * vocabulary (forest / towers / goose / tree). `Opening` and `Custom` keep
 * their existing names so an officer who adds one via `RaidStageAdd` sees a
 * consistent label.
 */
function stageKindLabel(kind: RaidStageKind): string {
  const labels = messages.value.stage
  if (kind === RaidStageKind.Forest) return labels.forest
  if (kind === RaidStageKind.MidPush) return labels.towers
  if (kind === RaidStageKind.LateDefense) return labels.goose
  if (kind === RaidStageKind.Endgame) return labels.tree
  if (kind === RaidStageKind.Custom) return labels.custom
  return labels.opening
}

function raidStageTabClass(stage: RaidStage): string {
  const active = props.activeStageId === stage.id
  if (active) {
    if (stage.kind === RaidStageKind.Forest) return 'border-success text-success bg-success/15'
    if (stage.kind === RaidStageKind.MidPush) return 'border-danger text-danger bg-danger/15'
    if (stage.kind === RaidStageKind.LateDefense) return 'border-success text-success bg-success/15'
    if (stage.kind === RaidStageKind.Endgame) return 'border-info text-info bg-info/15'
    if (stage.kind === RaidStageKind.Custom) return 'border-border-strong text-text-secondary bg-bg-surface'
    return 'border-accent text-accent bg-accent/15'
  }
  if (stage.kind === RaidStageKind.Opening) return 'border-border text-info bg-transparent hover:border-info hover:text-info'
  if (stage.kind === RaidStageKind.Forest) return 'border-border text-text-secondary bg-transparent hover:border-success hover:text-success'
  if (stage.kind === RaidStageKind.MidPush) return 'border-border text-text-secondary bg-transparent hover:border-danger hover:text-danger'
  if (stage.kind === RaidStageKind.LateDefense) return 'border-border text-text-secondary bg-transparent hover:border-success hover:text-success'
  if (stage.kind === RaidStageKind.Endgame) return 'border-border text-text-secondary bg-transparent hover:border-info hover:text-info'
  if (stage.kind === RaidStageKind.Custom) return 'border-dashed border-border text-text-muted bg-transparent hover:border-accent hover:text-accent'
  return 'border-border text-text-secondary bg-transparent'
}

function raidStageChipTitle(stage: RaidStage): string {
  return stage.notes ? `${stage.name || stageKindLabel(stage.kind)} — ${stage.notes}` : stageKindLabel(stage.kind)
}

function raidStageChipAriaLabel(stage: RaidStage): string {
  return messages.value.stage.ariaStage(stage.name || stageKindLabel(stage.kind))
}

function startRename(): void {
  if (!props.isOfficer) return
  const stage = activeCustomStage.value
  if (stage === null) return
  editingStageId.value = stage.id
  renameName.value = stage.name
  renameError.value = null
}

function closeRename(): void {
  editingStageId.value = null
  renameName.value = ''
  renameError.value = null
}

async function saveRename(): Promise<void> {
  if (!props.isOfficer) return
  const stage = activeCustomStage.value
  const name = renameName.value.trim()
  if (stage === null || editingStageId.value !== stage.id) {
    closeRename()
    return
  }
  if (name === '') {
    renameError.value = messages.value.stage.nameRequired
    return
  }
  if (Array.from(name).length > MAX_NAME_LEN) {
    renameError.value = messages.value.stage.nameTooLong(MAX_NAME_LEN)
    return
  }
  if (name === stage.name) {
    closeRename()
    return
  }

  renamePending.value = true
  renameError.value = null
  try {
    const succeeded = await store.updateStage(stage.id, { name })
    if (succeeded) {
      closeRename()
      return
    }
    renameError.value = store.error ?? messages.value.stage.renameError
  } catch (error: unknown) {
    renameError.value = error instanceof Error ? error.message : String(error)
  } finally {
    renamePending.value = false
  }
}
</script>
